import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '../../components/common/Button';
import StatsCard from '../../components/dashboard/StatsCard';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import {
    getProductById,
    updateProductStock,
    deleteProduct,
    getProductCategories,
    getProductBrands
} from '../../services/inventoryService';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';

const ViewProductPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { vendorProfile } = useAuth();
    const { showAlert } = useNotification();

    const [product, setProduct] = useState(null);
    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);
    const [purchaseInvoices, setPurchaseInvoices] = useState([]);
    const [purchaseStats, setPurchaseStats] = useState({
        totalInvoices: 0,
        totalQuantity: 0,
        totalAmount: 0,
        avgPurchaseRate: 0
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editingStock, setEditingStock] = useState(false);
    const [newStock, setNewStock] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    // Standard Barcode component for EAN-13
    const BarcodeDisplay = ({ barcode, size = 'lg' }) => {
        if (!barcode || barcode.length !== 13) {
            return <span className="text-xs sm:text-sm text-gray-400">Invalid barcode format</span>;
        }

        const width = size === 'lg' ? Math.min(300, window.innerWidth - 80) : Math.min(200, window.innerWidth - 100);
        const height = size === 'lg' ? 60 : 40; // Reduced height for mobile
        const fontSize = size === 'lg' ? 10 : 8; // Smaller font for mobile

        // EAN-13 bar patterns (keep existing pattern code)
        const leftOddPattern = {
            '0': '0001101', '1': '0011001', '2': '0010011', '3': '0111101',
            '4': '0100011', '5': '0110001', '6': '0101111', '7': '0111011',
            '8': '0110111', '9': '0001011'
        };

        const leftEvenPattern = {
            '0': '0100111', '1': '0110011', '2': '0011011', '3': '0100001',
            '4': '0011101', '5': '0111001', '6': '0000101', '7': '0010001',
            '8': '0001001', '9': '0010111'
        };

        const rightPattern = {
            '0': '1110010', '1': '1100110', '2': '1101100', '3': '1000010',
            '4': '1011100', '5': '1001110', '6': '1010000', '7': '1000100',
            '8': '1001000', '9': '1110100'
        };

        const firstDigitPatterns = {
            '0': 'OOOOOO', '1': 'OOEOEE', '2': 'OOEEOE', '3': 'OOEEEO',
            '4': 'OEOOEE', '5': 'OEEOOE', '6': 'OEEEOO', '7': 'OEOEOE',
            '8': 'OEOEEO', '9': 'OEEOEO'
        };

        const firstDigit = barcode[0];
        const leftDigits = barcode.substring(1, 7);
        const rightDigits = barcode.substring(7, 13);
        const pattern = firstDigitPatterns[firstDigit];

        let binaryString = '101';

        for (let i = 0; i < 6; i++) {
            const digit = leftDigits[i];
            if (pattern[i] === 'O') {
                binaryString += leftOddPattern[digit];
            } else {
                binaryString += leftEvenPattern[digit];
            }
        }

        binaryString += '01010';

        for (let i = 0; i < 6; i++) {
            const digit = rightDigits[i];
            binaryString += rightPattern[digit];
        }

        binaryString += '101';

        const bars = [];
        let x = 5; // Reduced padding for mobile
        const barWidth = (width - 10) / binaryString.length; // Reduced padding

        for (let i = 0; i < binaryString.length; i++) {
            if (binaryString[i] === '1') {
                bars.push(
                    <rect
                        key={i}
                        x={x}
                        y={5} // Reduced top padding
                        width={barWidth}
                        height={height - 20} // Adjusted height
                        fill="#000"
                    />
                );
            }
            x += barWidth;
        }

        return (
            <div className="flex flex-col items-center space-y-1 sm:space-y-2 w-full">
                <div className="bg-white p-2 sm:p-4 border border-gray-200 rounded-lg shadow-sm w-full max-w-sm overflow-hidden">
                    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} className="w-full">
                        {bars}
                        <text x="10" y={height - 3} fontSize={fontSize - 1} fill="#000" fontFamily="monospace">
                            {firstDigit}
                        </text>
                        <text x={width * 0.25} y={height - 3} fontSize={fontSize - 1} fill="#000" fontFamily="monospace" textAnchor="middle">
                            {leftDigits}
                        </text>
                        <text x={width * 0.75} y={height - 3} fontSize={fontSize - 1} fill="#000" fontFamily="monospace" textAnchor="middle">
                            {rightDigits}
                        </text>
                    </svg>
                </div>
                <div className="text-center">
                    <p className="text-xs font-mono break-all">{barcode}</p>
                    <p className="text-xs text-gray-400">EAN-13 Barcode</p>
                </div>
            </div>
        );
    };

    // Load product data and purchase invoices
    useEffect(() => {
        const loadProductAndData = async () => {
            try {
                setLoading(true);

                const [productData, categoriesData, brandsData, purchaseSnapshot] = await Promise.all([
                    getProductById(id),
                    getProductCategories(),
                    getProductBrands(),
                    getDocs(query(
                        collection(db, 'purchaseInvoices'),
                        where('vendorId', '==', vendorProfile.uid)
                    ))
                ]);

                setProduct(productData);
                setCategories(categoriesData || []);
                setBrands(brandsData || []);
                setNewStock(productData.stock?.toString() || '0');

                // Process purchase invoices
                const invoices = [];
                let totalQuantity = 0;
                let totalAmount = 0;

                purchaseSnapshot.docs.forEach(doc => {
                    const invoice = { id: doc.id, ...doc.data() };
                    invoice.items?.forEach(item => {
                        if (item.productId === id || (item.productName && productData.name?.toLowerCase().includes(item.productName.toLowerCase()))) {
                            invoices.push({
                                invoiceId: invoice.id,
                                invoiceNumber: invoice.invoiceNumber,
                                supplierName: invoice.supplierName,
                                purchaseDate: invoice.purchaseDate,
                                quantity: item.quantity,
                                rate: item.rate,
                                amount: item.amount,
                                hsnCode: item.hsnCode || 'N/A',
                                gstRate: item.gstRate || 0,
                                status: invoice.status,
                                createdAt: invoice.createdAt
                            });
                            totalQuantity += item.quantity || 0;
                            totalAmount += item.amount || 0;
                        }
                    });
                });

                invoices.sort((a, b) => new Date(b.purchaseDate) - new Date(a.purchaseDate));
                setPurchaseInvoices(invoices);
                setPurchaseStats({
                    totalInvoices: invoices.length,
                    totalQuantity,
                    totalAmount,
                    avgPurchaseRate: invoices.length > 0 ? totalAmount / totalQuantity : 0
                });

                setError('');
            } catch (error) {
                console.error('Error loading product:', error);
                setError('Failed to load product details. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        if (id && vendorProfile?.uid) {
            loadProductAndData();
        }
    }, [id, vendorProfile?.uid]);

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(amount || 0);
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // Get stock badge
    const getStockBadge = (stock, reorderLevel = 10) => {
        const stockNum = Number(stock) || 0;
        const reorderNum = Number(reorderLevel) || 10;

        if (stockNum <= 0) {
            return <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">Out of Stock</span>;
        } else if (stockNum <= reorderNum) {
            return <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">Low Stock: {stockNum}</span>;
        }
        return <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">In Stock: {stockNum}</span>;
    };

    // Get status badge
    const getStatusBadge = (status) => {
        const statusColors = {
            Active: 'bg-green-100 text-green-800',
            Obsolete: 'bg-gray-100 text-gray-800',
            Damaged: 'bg-red-100 text-red-800',
            Returned: 'bg-yellow-100 text-yellow-800',
            'Out of Stock': 'bg-red-100 text-red-800',
            Discontinued: 'bg-gray-100 text-gray-800',
        };
        const colorClass = statusColors[status] || 'bg-gray-100 text-gray-800';
        return <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${colorClass}`}>{status || 'Active'}</span>;
    };

    // Get brand information
    const getBrandInfo = (singleBrand) => {
        if (!singleBrand) {
            return {
                name: 'N/A',
                logo: `data:image/svg+xml;base64,${btoa(`
                    <svg width="120" height="60" xmlns="http://www.w3.org/2000/svg">
                      <rect width="120" height="60" fill="#E5E7EB"/>
                      <text x="60" y="35" text-anchor="middle" font-family="Arial" font-size="12" fill="#6B7280">No Brand</text>
                    </svg>
                  `)}`,
                description: null,
                website: null
            };
        }

        if (!brands || brands.length === 0) {
            const brandName = typeof singleBrand === 'string' ? singleBrand : 'Brand';
            return {
                name: brandName,
                logo: `data:image/svg+xml;base64,${btoa(`
                    <svg width="120" height="60" xmlns="http://www.w3.org/2000/svg">
                      <rect width="120" height="60" fill="#F3F4F6"/>
                      <text x="60" y="35" text-anchor="middle" font-family="Arial" font-size="11" fill="#374151">${brandName}</text>
                    </svg>
                  `)}`,
                description: null,
                website: null
            };
        }

        let brandInfo = null;

        if (typeof singleBrand === 'string') {
            brandInfo = brands.find(b => b.id === singleBrand || (b.name && b.name.toLowerCase() === singleBrand.toLowerCase()));
        } else if (singleBrand && typeof singleBrand === 'object') {
            brandInfo = singleBrand;
        }

        if (brandInfo) {
            return {
                name: brandInfo.name || 'Unknown Brand',
                logo: brandInfo.logo || `data:image/svg+xml;base64,${btoa(`
                    <svg width="120" height="60" xmlns="http://www.w3.org/2000/svg">
                      <rect width="120" height="60" fill="#F9FAFB" stroke="#D1D5DB"/>
                      <text x="60" y="35" text-anchor="middle" font-family="Arial" font-size="11" fill="#374151">${brandInfo.name}</text>
                    </svg>
                  `)}`,
                description: brandInfo.description || null,
                website: brandInfo.website || null
            };
        }

        const fallbackName = typeof singleBrand === 'string' ? singleBrand : 'Unknown Brand';
        return {
            name: fallbackName,
            logo: `data:image/svg+xml;base64,${btoa(`
                <svg width="120" height="60" xmlns="http://www.w3.org/2000/svg">
                  <rect width="120" height="60" fill="#F9FAFB" stroke="#D1D5DB"/>
                  <text x="60" y="35" text-anchor="middle" font-family="Arial" font-size="11" fill="#374151">${fallbackName}</text>
                </svg>
              `)}`,
            description: null,
            website: null
        };
    };

    // Get category information
    const getCategoryInfo = (category) => {
        if (!category) {
            return {
                name: 'N/A',
                image: `https://via.placeholder.com/40x40/3B82F6/FFFFFF?text=C`,
                description: null
            };
        }

        let categoryInfo = null;

        if (typeof category === 'string') {
            categoryInfo = categories.find(c => c.id === category || c.name.toLowerCase() === category.toLowerCase());
        } else if (category && typeof category === 'object') {
            categoryInfo = category;
        }

        if (categoryInfo) {
            return {
                name: categoryInfo.name || 'Unknown Category',
                image: categoryInfo.image || categoryInfo.imageUrl || categoryInfo.logo || `https://via.placeholder.com/40x40/3B82F6/FFFFFF?text=${encodeURIComponent(categoryInfo.name?.charAt(0).toUpperCase() || 'C')}`,
                description: categoryInfo.description || null
            };
        }

        const fallbackName = typeof category === 'string' ? category : 'Unknown Category';
        return {
            name: fallbackName,
            image: `https://via.placeholder.com/40x40/3B82F6/FFFFFF?text=${encodeURIComponent(fallbackName.charAt(0).toUpperCase())}`,
            description: null
        };
    };

    // Handle stock update
    const handleStockUpdate = async () => {
        try {
            const stockValue = parseInt(newStock, 10);
            if (isNaN(stockValue) || stockValue < 0) {
                showAlert('Please enter a valid stock number', 'error');
                return;
            }

            await updateProductStock(id, stockValue);
            setProduct(prev => ({ ...prev, stock: stockValue }));
            setEditingStock(false);
            showAlert('Stock updated successfully', 'success');
        } catch (error) {
            console.error('Error updating stock:', error);
            showAlert('Failed to update stock', 'error');
        }
    };

    // Handle product deletion
    const handleDelete = async () => {
        try {
            await deleteProduct(id, vendorProfile.uid);
            showAlert('Product deleted successfully', 'success');
            navigate('/inventory');
        } catch (error) {
            console.error('Error deleting product:', error);
            showAlert('Failed to delete product', 'error');
            setShowDeleteModal(false);
        }
    };

    // Handle image error
    const handleImageError = (e) => {
        e.target.style.display = 'none';
        e.target.nextElementSibling.style.display = 'flex';
    };

    // Print barcode function
    const handlePrintBarcode = () => {
        if (!product.barcode) return;

        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>Barcode - ${product.name}</title>
                <style>
                    body {
                        font-family: Arial, sans-serif;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        min-height: 100vh;
                        margin: 0;
                        background: white;
                    }
                    .barcode-container {
                        text-align: center;
                        padding: 20px;
                        border: 2px solid #000;
                    }
                    .product-name {
                        font-size: 16px;
                        font-weight: bold;
                        margin-bottom: 10px;
                    }
                    .barcode-number {
                        font-family: monospace;
                        font-size: 14px;
                        margin-top: 10px;
                    }
                    @media print {
                        body { margin: 0; }
                        .barcode-container { border: 1px solid #000; }
                    }
                </style>
            </head>
            <body>
                <div class="barcode-container">
                    <div class="product-name">${product.name}</div>
                    <svg width="300" height="80" viewBox="0 0 300 80">
                        ${product.barcode.split('').map((digit, index) =>
            `<rect x="${10 + index * 28}" y="10" width="${Math.max(2, parseInt(digit) + 2)}" height="50" fill="#000"/>`
        ).join('')}
                        <text x="150" y="75" text-anchor="middle" font-size="12" font-family="monospace">${product.barcode}</text>
                    </svg>
                    <div class="barcode-number">${product.barcode}</div>
                </div>
            </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.print();
        printWindow.close();
    };

    if (loading) {
        return (
            <DashboardLayout>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="py-6">
                        <div className="flex justify-center items-center h-64">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
                            <span className="ml-3 text-gray-600">Loading product details...</span>
                        </div>
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    if (error || !product) {
        return (
            <DashboardLayout>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="py-6">
                        <div className="bg-red-50 border border-red-200 rounded-md p-4">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <h3 className="text-sm font-medium text-red-800">{error || 'Product not found'}</h3>
                                </div>
                            </div>
                        </div>
                        <div className="mt-6">
                            <Button onClick={() => navigate('/inventory')}>
                                Back to Inventory
                            </Button>
                        </div>
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="py-6">
                    <div className="flex justify-between items-start">
                        <div>
                            <nav className="flex mb-4" aria-label="Breadcrumb">
                                <ol className="flex items-center space-x-4">
                                    <li>
                                        <Link to="/inventory" className="text-gray-400 hover:text-gray-500">
                                            <span>Inventory</span>
                                        </Link>
                                    </li>
                                    <li>
                                        <div className="flex items-center">
                                            <svg className="flex-shrink-0 h-5 w-5 text-gray-300" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                                                <path d="M5.555 17.776l8-16 .894.448-8 16-.894-.448z" />
                                            </svg>
                                            <span className="ml-4 text-sm font-medium text-gray-500">Product Details</span>
                                        </div>
                                    </li>
                                </ol>
                            </nav>
                            <h1 className="text-3xl font-bold text-gray-900">{product.name}</h1>
                            <p className="mt-1 text-sm text-gray-500">
                                Part Number: {product.partNumber || 'N/A'}
                            </p>
                        </div>
                        <div className="flex space-x-3">
                            <Link to={`/inventory/edit/${product.id}`}>
                                <Button variant="secondary">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                                        <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                    </svg>
                                    Edit
                                </Button>
                            </Link>
                            <Button variant="danger" onClick={() => setShowDeleteModal(true)}>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                Delete
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Purchase Statistics */}
                <div className="mb-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Purchase Statistics</h3>
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                       <StatsCard
  title="Invoices"
  value={purchaseStats.totalInvoices}
  subtitle="Total Purchased"
  icon={
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5 lg:h-6 lg:w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
    </svg>
  }
/>

<StatsCard
  title="Quantity"
  value={purchaseStats.totalQuantity}
  subtitle="Total Purchased"
  icon={
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5 lg:h-6 lg:w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
    </svg>
  }
/>

<StatsCard
  title="Amount"
  value={formatCurrency(purchaseStats.totalAmount)}
  subtitle="Total Purchase"
  icon={
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5 lg:h-6 lg:w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  }
/>

<StatsCard
  title="Avg Rate"
  value={formatCurrency(purchaseStats.avgPurchaseRate)}
  subtitle="Average Purchase"
  icon={
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5 lg:h-6 lg:w-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2M9 19" />
    </svg>
  }
/>
                    </div>
                </div>

                {/* Main Content */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Product Images and Barcode */}
                    <div className="lg:col-span-1">
                        {/* Product Images */}
                        <div className="bg-white shadow rounded-lg p-6 mb-6">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Product Images</h3>
                            <div className="space-y-4">
                                {product.imageUrls && product.imageUrls.length > 0 ? (
                                    product.imageUrls.map((imageUrl, index) => (
                                        <div key={index} className="relative">
                                            <img
                                                className="w-full h-48 object-cover rounded-lg"
                                                src={imageUrl}
                                                alt={`${product.name} - Image ${index + 1}`}
                                                onError={handleImageError}
                                            />
                                            <div className="w-full h-48 bg-gray-200 rounded-lg flex items-center justify-center text-gray-500 hidden absolute inset-0">
                                                <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                </svg>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="w-full h-48 bg-gray-200 rounded-lg flex items-center justify-center text-gray-500">
                                        <div className="text-center">
                                            <svg className="h-12 w-12 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                            </svg>
                                            <p className="text-sm">No images available</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Barcode Section */}
                        <div className="bg-white shadow rounded-lg p-4 sm:p-6">
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 space-y-2 sm:space-y-0">
                                <h3 className="text-lg font-medium text-gray-900">Product Barcode</h3>
                                {product.barcode && (
                                    <Button
                                        variant="secondary"
                                        onClick={handlePrintBarcode}
                                        className="text-sm w-full sm:w-auto"
                                    >
                                        <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                                        </svg>
                                        Print
                                    </Button>
                                )}
                            </div>
                            <div className="flex justify-center w-full">
                                <BarcodeDisplay barcode={product.barcode} size="lg" />
                            </div>
                        </div>
                    </div>

                    {/* Product Details */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Basic Information */}
                        <div className="bg-white shadow rounded-lg p-4 sm:p-6">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
                            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                <div>
                                    <dt className="text-sm font-medium text-gray-500">Product Name</dt>
                                    <dd className="mt-1 text-sm text-gray-900 break-words">{product.name || 'N/A'}</dd>
                                </div>
                                <div>
                                    <dt className="text-sm font-medium text-gray-500">Part Number</dt>
                                    <dd className="mt-1 text-sm text-gray-900 font-mono break-all">{product.partNumber || 'N/A'}</dd>
                                </div>
                                <div>
                                    <dt className="text-sm font-medium text-gray-500">SKU</dt>
                                    <dd className="mt-1 text-sm text-gray-900 font-mono break-all">{product.sku || 'N/A'}</dd>
                                </div>
                                <div>
                                    <dt className="text-sm font-medium text-gray-500">Barcode</dt>
                                    <dd className="mt-1 text-sm text-gray-900 font-mono break-all">{product.barcode || 'N/A'}</dd>
                                </div>
                                <div>
                                    <dt className="text-sm font-medium text-gray-500">HSN Code</dt>
                                    <dd className="mt-1 text-sm text-gray-900 break-all">{product.hsnCode || 'N/A'}</dd>
                                </div>
                                <div>
                                    <dt className="text-sm font-medium text-gray-500">Status</dt>
                                    <dd className="mt-1">{getStatusBadge(product.status)}</dd>
                                </div>
                                {product.description && (
                                    <div className="sm:col-span-2">
                                        <dt className="text-sm font-medium text-gray-500">Description</dt>
                                        <dd className="mt-1 text-sm text-gray-900 break-words whitespace-pre-wrap">{product.description}</dd>
                                    </div>
                                )}
                            </dl>
                        </div>

                        {/* Categories and Brand */}
                        <div className="bg-white shadow rounded-lg p-6">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Classification</h3>
                            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <div>
                                    <dt className="text-sm font-medium text-gray-500 mb-3">Categories</dt>
                                    <dd className="mt-1">
                                        {product.categories && product.categories.length > 0 ? (
                                            <div className="space-y-3">
                                                {product.categories.map((category, index) => {
                                                    const categoryInfo = getCategoryInfo(category);
                                                    return (
                                                        <div key={index} className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
                                                            <div className="flex-shrink-0">
                                                                <div className="relative">
                                                                    <img
                                                                        className="h-10 w-10 rounded-lg object-cover"
                                                                        src={categoryInfo.image}
                                                                        alt={categoryInfo.name}
                                                                        onError={(e) => {
                                                                            e.target.style.display = 'none';
                                                                            e.target.nextElementSibling.style.display = 'flex';
                                                                        }}
                                                                    />
                                                                    <div className="h-10 w-10 rounded-lg bg-blue-200 items-center justify-center text-blue-600 hidden absolute inset-0 flex">
                                                                        <span className="text-sm font-semibold">{categoryInfo.name.charAt(0).toUpperCase()}</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="flex-1">
                                                                <span className="text-sm font-medium text-blue-900">{categoryInfo.name}</span>
                                                                {categoryInfo.description && (
                                                                    <p className="text-xs text-blue-700 mt-1">{categoryInfo.description}</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <span className="text-sm text-gray-400">N/A</span>
                                        )}
                                    </dd>
                                </div>
                                <div>
                                    <dt className="text-sm font-medium text-gray-500 mb-3">Brands</dt>
                                    <dd className="mt-1">
                                        {product.brand || product.brands ? (
                                            (() => {
                                                const brandData = product.brand || product.brands;
                                                let brandsToShow = [];

                                                if (typeof brandData === 'string') {
                                                    brandsToShow = [brandData];
                                                } else if (Array.isArray(brandData)) {
                                                    brandsToShow = brandData;
                                                } else if (brandData && typeof brandData === 'object') {
                                                    brandsToShow = [brandData];
                                                }

                                                return (
                                                    <div className="space-y-3">
                                                        {brandsToShow.map((brand, index) => {
                                                            const brandInfo = getBrandInfo(brand);

                                                            return (
                                                                <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                                                                    <div className="flex-shrink-0">
                                                                        <div className="relative">
                                                                            <img
                                                                                className="h-12 w-20 object-contain rounded"
                                                                                src={brandInfo.logo}
                                                                                alt={brandInfo.name}
                                                                                onError={(e) => {
                                                                                    e.target.style.display = 'none';
                                                                                    e.target.nextElementSibling.style.display = 'flex';
                                                                                }}
                                                                            />
                                                                            <div className="h-12 w-20 bg-gray-200 items-center justify-center text-gray-600 hidden absolute inset-0 flex rounded">
                                                                                <span className="text-xs font-semibold text-center">
                                                                                    {brandInfo.name.substring(0, 8)}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex-1">
                                                                        <div className="text-sm font-medium text-gray-900">
                                                                            {brandInfo.name}
                                                                        </div>
                                                                        {brandInfo.description && (
                                                                            <p className="text-xs text-gray-600 mt-1">{brandInfo.description}</p>
                                                                        )}
                                                                        {brandInfo.website && (
                                                                            <a
                                                                                href={brandInfo.website}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                className="text-xs text-blue-600 hover:text-blue-800 mt-1 inline-block"
                                                                            >
                                                                                Visit Website →
                                                                            </a>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                );
                                            })()
                                        ) : (
                                            <span className="text-sm text-gray-400">N/A</span>
                                        )}
                                    </dd>
                                </div>
                                {product.modelCompatibility && product.modelCompatibility.length > 0 && (
                                    <div className="sm:col-span-2">
                                        <dt className="text-sm font-medium text-gray-500 mb-3">Model Compatibility</dt>
                                        <dd className="mt-1">
                                            <div className="flex flex-wrap gap-2">
                                                {product.modelCompatibility.map((model, index) => (
                                                    <span key={index} className="inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium bg-purple-100 text-purple-800 border border-purple-200">
                                                        <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                                        </svg>
                                                        {model}
                                                    </span>
                                                ))}
                                            </div>
                                        </dd>
                                    </div>
                                )}
                            </dl>
                        </div>


                        {/* Purchase History */}
                        <div className="bg-white shadow rounded-lg p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                                    <svg className="h-5 w-5 mr-2 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                                    </svg>
                                    Purchase History ({purchaseStats.totalInvoices} invoices)
                                </h3>
                                <Link
                                    to={`/billing?tab=create-purchase&productId=${product.id}&productName=${encodeURIComponent(product.name)}`}
                                    className="text-sm text-orange-600 hover:text-orange-800 font-medium flex items-center"
                                >
                                    <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                    </svg>
                                    Add Purchase
                                </Link>
                            </div>
                            {purchaseInvoices.length > 0 ? (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Invoice</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">HSN Code</th>
                                                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Qty</th>
                                                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Rate</th>
                                                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">GST Rate</th>
                                                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                                                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                                                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {purchaseInvoices.map((purchase, index) => (
                                                <tr key={index} className="hover:bg-gray-50">
                                                    <td className="px-3 py-2 text-xs">
                                                        <Link
                                                            to={`/billing/purchase/${purchase.invoiceId}`}
                                                            className="text-blue-600 hover:text-blue-800 font-medium"
                                                        >
                                                            {purchase.invoiceNumber}
                                                        </Link>
                                                    </td>
                                                    <td className="px-3 py-2 text-xs text-gray-900">{purchase.supplierName}</td>
                                                    <td className="px-3 py-2 text-xs text-gray-900">{formatDate(purchase.purchaseDate)}</td>
                                                    <td className="px-3 py-2 text-xs text-gray-900 text-center">{purchase.hsnCode}</td>
                                                    <td className="px-3 py-2 text-xs text-gray-900 text-right">{purchase.quantity}</td>
                                                    <td className="px-3 py-2 text-xs text-gray-900 text-right">{formatCurrency(purchase.rate)}</td>
                                                    <td className="px-3 py-2 text-xs text-gray-900 text-right">{purchase.gstRate}%</td>
                                                    <td className="px-3 py-2 text-xs text-gray-900 font-medium text-right">{formatCurrency(purchase.amount)}</td>
                                                    <td className="px-3 py-2 text-xs text-center">
                                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${purchase.status === 'received' ? 'bg-green-100 text-green-800' :
                                                            purchase.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                                                'bg-gray-100 text-gray-800'
                                                            }`}>
                                                            {purchase.status || 'N/A'}
                                                        </span>
                                                    </td>
                                                    <td className="px-3 py-2 text-xs text-center">
                                                        <Link
                                                            to={`/billing/purchase/${purchase.invoiceId}`}
                                                            className="text-blue-600 hover:text-blue-800"
                                                            title="View invoice"
                                                        >
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                            </svg>
                                                        </Link>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-center py-8">
                                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                                    </svg>
                                    <p className="mt-2 text-sm text-gray-500">No purchase history found</p>
                                    <p className="text-xs text-gray-400 mb-4">Create a purchase invoice to track supplier purchases</p>
                                    <Link
                                        to={`/billing?tab=create-purchase&productId=${product.id}&productName=${encodeURIComponent(product.name)}`}
                                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                                    >
                                        <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                        </svg>
                                        Create First Purchase
                                    </Link>
                                </div>
                            )}
                        </div>

                        {/* Pricing and Inventory */}
                        <div className="bg-white shadow rounded-lg p-6">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Pricing & Inventory</h3>
                            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <dt className="text-sm font-medium text-gray-500">Selling Price</dt>
                                    <dd className="mt-1 text-lg font-semibold text-gray-900">{formatCurrency(product.price || product.sellingPrice)}</dd>
                                </div>
                                {product.comparePrice && (
                                    <div>
                                        <dt className="text-sm font-medium text-gray-500">Compare Price</dt>
                                        <dd className="mt-1 text-sm text-gray-500 line-through">{formatCurrency(product.comparePrice)}</dd>
                                    </div>
                                )}
                                <div>
                                    <dt className="text-sm font-medium text-gray-500">Cost Price</dt>
                                    <dd className="mt-1 text-sm text-gray-900">{product.cost ? formatCurrency(product.cost) : 'N/A'}</dd>
                                </div>
                                <div>
                                    <dt className="text-sm font-medium text-gray-500">Current Stock</dt>
                                    <dd className="mt-1">
                                        {editingStock ? (
                                            <div className="flex items-center space-x-2">
                                                <input
                                                    type="number"
                                                    min="0"
                                                    value={newStock}
                                                    onChange={(e) => setNewStock(e.target.value)}
                                                    className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                                                />
                                                <button onClick={handleStockUpdate} className="text-green-600 hover:text-green-900" title="Save">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                </button>
                                                <button onClick={() => setEditingStock(false)} className="text-red-600 hover:text-red-900" title="Cancel">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                </button>
                                            </div>
                                        ) : (
                                            <div onClick={() => setEditingStock(true)} className="cursor-pointer" title="Click to edit stock">
                                                {getStockBadge(product.stock, product.reorderLevel)}
                                            </div>
                                        )}
                                    </dd>
                                </div>
                                <div>
                                    <dt className="text-sm font-medium text-gray-500">Reorder Level</dt>
                                    <dd className="mt-1 text-sm text-gray-900">{product.reorderLevel || product.lowStockThreshold || 'N/A'}</dd>
                                </div>
                                <div>
                                    <dt className="text-sm font-medium text-gray-500">Location</dt>
                                    <dd className="mt-1 text-sm text-gray-900">{product.location || 'N/A'}</dd>
                                </div>
                            </dl>
                        </div>



                        {/* Physical Properties */}
                        {(product.weight || product.dimensions) && (
                            <div className="bg-white shadow rounded-lg p-6">
                                <h3 className="text-lg font-medium text-gray-900 mb-4">Physical Properties</h3>
                                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {product.weight && (
                                        <div>
                                            <dt className="text-sm font-medium text-gray-500">Weight</dt>
                                            <dd className="mt-1 text-sm text-gray-900">{product.weight} kg</dd>
                                        </div>
                                    )}
                                    {product.dimensions && (product.dimensions.length || product.dimensions.width || product.dimensions.height) && (
                                        <div>
                                            <dt className="text-sm font-medium text-gray-500">Dimensions (L × W × H)</dt>
                                            <dd className="mt-1 text-sm text-gray-900">
                                                {product.dimensions.length || '0'} × {product.dimensions.width || '0'} × {product.dimensions.height || '0'} cm
                                            </dd>
                                        </div>
                                    )}
                                </dl>
                            </div>
                        )}

                        {/* Warranty and Additional Info */}
                        {(product.warrantyMonths || product.hsnCode || product.createdAt) && (
                            <div className="bg-white shadow rounded-lg p-6">
                                <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Information</h3>
                                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {product.warrantyMonths && (
                                        <div>
                                            <dt className="text-sm font-medium text-gray-500">Warranty Period</dt>
                                            <dd className="mt-1 text-sm text-gray-900">{product.warrantyMonths} months</dd>
                                        </div>
                                    )}
                                    {product.hsnCode && (
                                        <div>
                                            <dt className="text-sm font-medium text-gray-500">HSN Code</dt>
                                            <dd className="mt-1 text-sm text-gray-900 font-mono">{product.hsnCode}</dd>
                                        </div>
                                    )}
                                    {product.createdAt && (
                                        <div>
                                            <dt className="text-sm font-medium text-gray-500">Created</dt>
                                            <dd className="mt-1 text-sm text-gray-900">{formatDate(product.createdAt)}</dd>
                                        </div>
                                    )}
                                    {product.updatedAt && (
                                        <div>
                                            <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
                                            <dd className="mt-1 text-sm text-gray-900">{formatDate(product.updatedAt)}</dd>
                                        </div>
                                    )}
                                </dl>
                            </div>
                        )}

                        {/* Specifications */}
                        {product.specifications && product.specifications.length > 0 && (
                            <div className="bg-white shadow rounded-lg p-6">
                                <h3 className="text-lg font-medium text-gray-900 mb-4">Specifications</h3>
                                <dl className="space-y-2">
                                    {product.specifications.map((spec, index) => (
                                        <div key={index} className="grid grid-cols-3 gap-4 py-2 border-b border-gray-100 last:border-b-0">
                                            <dt className="text-sm font-medium text-gray-500">{spec.name}</dt>
                                            <dd className="text-sm text-gray-900 col-span-2">{spec.value}</dd>
                                        </div>
                                    ))}
                                </dl>
                            </div>
                        )}

                        {/* Notes */}
                        {product.notes && (
                            <div className="bg-white shadow rounded-lg p-6">
                                <h3 className="text-lg font-medium text-gray-900 mb-4">Notes</h3>
                                <p className="text-sm text-gray-900 whitespace-pre-wrap">{product.notes}</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Delete Confirmation Modal */}
                {showDeleteModal && (
                    <div className="fixed z-10 inset-0 overflow-y-auto">
                        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                                <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                            </div>

                            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true"></span>

                            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                    <div className="sm:flex sm:items-start">
                                        <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                            <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                            </svg>
                                        </div>
                                        <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                            <h3 className="text-lg leading-6 font-medium text-gray-900">Delete Product</h3>
                                            <div className="mt-2">
                                                <p className="text-sm text-gray-500">
                                                    Are you sure you want to delete "{product.name}"? This action cannot be undone.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                    <button
                                        type="button"
                                        className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                                        onClick={handleDelete}
                                    >
                                        Delete
                                    </button>
                                    <button
                                        type="button"
                                        className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                        onClick={() => setShowDeleteModal(false)}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
};

export default ViewProductPage;